import React, { useState, useEffect, useMemo, useRef } from "react";
import DashboardContent from "../../components/DashboardContent";
import useCsr from "../../hooks/useCsr";
import { useParams } from "react-router-dom";
import DataMappingContent from "../../components/DataMappingContent";
import { CsrAPI } from "../../apis/CsrAPI";
import { WIZARD_SECTIONS } from "../../utils/wizardSections";
import { toast, ToastContainer } from "react-toastify";
import * as Sentry from "@sentry/react";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";
// import useQuestions from "../../hooks/useQuestions";
import { useCsrDropdownData } from "../../hooks/useCsrDropdownData";
import { usePermissions } from "../../contexts/PermissionsContext";
import { PERMISSIONS } from "../../utils/permissions";
import { FILES_STATUS } from "../../utils/Constants";

const DataMapping = () => {
  const { setAjaxLoading, filesStatus } = useCsr();
  // readyEmpty is set by getDocuments once the study's document list has
  // come back with nothing in it. /status can only ever report
  // study_files: false in that case, so there is nothing to wait for.
  const hasNoUploadedFiles = filesStatus === FILES_STATUS.readyEmpty;
  const { studyId } = useParams();
  const { hasPermission } = usePermissions();
  const canReadWizards = hasPermission(PERMISSIONS.csrWizardsRead);
  const [selections, setSelections] = useState({
    demography: [],
    diseaseCharacteristics: [],
    death: [],
    discontinuation: [],
    frequency: [],
    otherSignificant: [],
    safetySummary: [],
    serious: [],
    severity: [],
    specialInterest: [],
    treatmentRelated: [],
    protocolDeviations: [],
    primaryEfficacy: [],
    secondaryEfficacy: [],
    dsHematology: [],
    avHematology: [],
    stHematology: [],
    dsSerumChemistry: [],
    avSerumChemistry: [],
    stSerumChemistry: [],
    dsUrinalysis: [],
    avUrinalysis: [],
    stUrinalysis: [],
    dsSpecialInterest: [],
    avSpecialInterest: [],
    stSpecialInterest: [],
    dsVitalSigns: [],
    avVitalSigns: [],
    stVitalSigns: [],
    dsEcg: [],
    avEcg: [],
    stEcg: [],
    medicalHistory: [],
    diseaseMedicalHistory: [],
    concomitantTherapy: [],
    diseaseConcomitantTherapy: [],
    exposure: [],
    compliance: [],
    diseaseCompliance: [],
  });
  const errRef = useRef();
  // const { formFilledData, wizardFormFilledData } = useQuestions();
  // const [qFormSavedData, setQFormSavedData] = useState(null);
  // const [wizardFormSavedData, setWizardFormSavedData] = useState(null);

  const [errMsg, setErrMsg] = useState("");

  // Status polling and the tables fetch are owned by the shared orchestrator
  // (startStudyDataLoad, dispatched once per study by QuestionsProvider).
  // This page is a read-only consumer, so landing here adds no extra
  // requests to GET /studies/{study_id}/status.
  const {
    items: tableNames,
    isReady: isTableDataReady,
    error: tableDataError,
  } = useCsrDropdownData({ kind: "table" });

  const [savedMappingError, setSavedMappingError] = useState(null);
  const fetchedQuestionsForRef = useRef(null);

  // Saved data mapping doesn't depend on document parsing, so it is fetched
  // once per study as soon as the page mounts. The ref keeps it to a single
  // request even though the component re-renders on every polling update.
  useEffect(() => {
    if (!studyId || fetchedQuestionsForRef.current === studyId) return;
    // csr:wizards:read gates the section fetch, so the page starts from an
    // empty mapping instead of calling the endpoint.
    if (!canReadWizards) return;
    fetchedQuestionsForRef.current = studyId;

    const fetchSavedDataMapping = async () => {
      try {
        setSavedMappingError(null);
        // {} means the page has never been saved, not an error.
        const dataMapping = await CsrAPI.getWizardSection({
          studyId,
          section: WIZARD_SECTIONS.dataMapping,
        });
        if (!dataMapping || Object.keys(dataMapping).length === 0) return;

        setSelections(prev => {
          // Don't clobber anything the user has already picked in this session.
          const hasAnySavedData = Object.values(prev).some(
            selection => Array.isArray(selection) && selection.length > 0
          );
          if (hasAnySavedData) return prev;

          const next = { ...prev };
          Object.keys(prev).forEach(key => {
            next[key] = dataMapping[key] || prev[key];
          });
          return next;
        });
      } catch (err) {
        console.error("Error fetching saved data mapping:", err);
        setSavedMappingError(
          "Failed to load your saved data mapping. Please try again."
        );
      }
    };

    fetchSavedDataMapping();
  }, [studyId, canReadWizards]);

  // Shared API data for all dropdowns, derived from the csrData store.
  // While the backend reports study_files: false the store stays in
  // "polling", which surfaces here as isLoadingData -> DataMappingContent
  // renders its loader — unless no files were uploaded at all, in which
  // case the loader would never go away.
  const sharedTableData = useMemo(
    () => ({
      tableOptions: tableNames
        .filter(item => item && typeof item === "string")
        .map(item => ({
          value: item,
          label: item,
          valueHelper: item,
        })),
      isDataReady: isTableDataReady,
      // Loader stays up until the tables are in, unless the status/tables
      // request itself failed — then the error is the more useful message.
      isLoadingData:
        !isTableDataReady && !tableDataError && !hasNoUploadedFiles,
      dataError: tableDataError || savedMappingError || null,
    }),
    [
      tableNames,
      isTableDataReady,
      tableDataError,
      savedMappingError,
      hasNoUploadedFiles,
    ]
  );

  // Handle CSR File button click - triggers API call to save table selections
  const handleCsrFileClick = async () => {
    // csr:wizards:update gates the PATCH; the Save button is disabled without
    // it and Next navigates without saving.
    if (!hasPermission(PERMISSIONS.csrWizardsUpdate)) return;

    try {
      // Return the Promise so it can be awaited by navigation handlers
      return CsrAPI.saveWizardSection({
        studyId,
        section: WIZARD_SECTIONS.dataMapping,
        sectionData: { ...selections },
      })
        .then(() => {
          setErrMsg("");
          toast.success("Data saved successfully!");
          return Promise.resolve(); // Explicitly return resolved promise for success
        })
        .catch(error => {
          if (isProduction) {
            Sentry.captureException(error);
          }
          if (!error?.response) {
            setErrMsg("No Server Response");
          } else if (error.response?.status === 401) {
            setErrMsg("Invalid user credentials");
          } else {
            setErrMsg("Failed to save questions");
          }
          errRef.current?.focus();
          return Promise.reject(error); // Re-throw the error for navigation handler
        })
        .finally(() => {
          setAjaxLoading(false);
        });
    } catch (error) {
      console.error("Failed to save table selections:", error);
    }
  };

  const PageContent = () => {
    return (
      <div>
        <span
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive">
          {errMsg}
        </span>
        <DataMappingContent
          selections={selections}
          setSelections={setSelections}
          sharedTableData={sharedTableData}
        />
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={
        "Please complete the Data Mapping before proceeding to CSR generation"
      }
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Comparative PD",
        buttonGoToAction: "/csr/" + studyId + "/comppharmacodynamics",
      }}
      nextButton={{
        buttonTitle: "CSR File",
        buttonGoToAction: "/csr/" + studyId + "/file",
      }}
      saveButton={true}
      saveButtonHandler={handleCsrFileClick}
      savePermission={PERMISSIONS.csrWizardsUpdate}
    />
  );
};

export default DataMapping;
